import iconsPng from '@/assets/img/png/icons';
import { THealthDomain, TSubdomainScore } from '@/types/Screener.types';
import { getColorFromPercentage } from '@/utils/getColorFromPercentage';
import { displayCapitalizedUnderscoredString } from '@/utils/stringUtils';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  HStack,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import { capitalize } from 'lodash';
import React from 'react';
import CircleFillOutWithText from '../../WellnessStateCircle/CircleFillOutWithText';

type Props = {
  domain: THealthDomain;
  domainScore: number;
  subdomainScore?: TSubdomainScore;
};

const SingleDomain = ({ domain, domainScore, subdomainScore = {} }: Props) => {
  const totalScoreX10 = domainScore > 10 ? domainScore : domainScore * 10;
  const color = getColorFromPercentage(totalScoreX10);

  const [isAccordionExpanded, setIsAccordionExpanded] = React.useState(false);

  const onAccordionToggle = (expandedIndex: number) => {
    const isExpanded = expandedIndex === 0;
    setIsAccordionExpanded(isExpanded);
  };

  const isAccordionDisabled = Object.keys(subdomainScore).length === 0;

  return (
    <VStack width={'362px'} height={'100%'} display={'flex'} direction={'column'}>
      <VStack
        alignItems={'flex-start'}
        backgroundColor={'white'}
        borderRadius={'12px'}
        paddingX={'14px'}
        paddingY={'16px'}
        width={'full'}
        boxShadow={`0px 10px 30px 0px #4E1AEF0D`}
        gap={'20px'}
      >
        <HStack gap={'14px'} width={'full'}>
          <Image src={iconsPng.wellnessDomainsPurple[domain]} width={'28px'} height={'28px'} />
          <VStack alignItems={'flex-start'} width={'100%'} gap={'12px'}>
            <HStack justifyContent={'space-between'} width={'100%'}>
              <Text fontSize={'18px'} variant={'urbanistSmall'} color={'text.mediumBlue'} fontWeight={'600'}>
                {capitalize(domain)}
              </Text>
              <Text fontSize={'18px'} variant={'urbanistSmall'} color={'text.mediumBlue'} fontWeight={'800'}>
                {totalScoreX10.toFixed()}
              </Text>
            </HStack>
            <HStack width={'100%'} height={'8px'} borderRadius={'10px'} bg="#F4F4F4">
              <VStack borderRadius={'10px'} height={'8px'} width={`${totalScoreX10}%`} bg={color} />
            </HStack>
          </VStack>
        </HStack>
        <Accordion
          width={'full'}
          borderColor={'white'}
          allowToggle
          onChange={onAccordionToggle}
          defaultIndex={isAccordionExpanded ? 0 : -1}
        >
          <AccordionItem isDisabled={isAccordionDisabled}>
            <AccordionButton paddingX={'4px'} paddingY={0}>
              <HStack width={'full'} justifyContent={'space-between'}>
                <Text fontSize={'16px'} variant={'urbanistSmall'} color={'text.mediumBlue'} fontWeight={'700'}>
                  {Object.keys(subdomainScore).length} concerns
                </Text>
                {!isAccordionDisabled && (
                  <HStack bg="#EFF7FE" paddingX={'10px'} paddingY={'6px'} borderRadius={'full'} gap={0}>
                    <Text fontSize={'10px'} variant={'urbanistSmall'} color={'text.mediumGray'} fontWeight={'700'}>
                      {isAccordionExpanded ? 'COLLAPSE' : 'EXPAND'}
                    </Text>
                    <AccordionIcon color={'text.mediumGray'} width={'18px'} />
                  </HStack>
                )}
              </HStack>
            </AccordionButton>
            <AccordionPanel padding={0} paddingTop={'20px'}>
              {Object.entries(subdomainScore)?.map(([key, value]) => {
                return (
                  <VStack
                    alignItems={'flex-start'}
                    key={key}
                    padding={0}
                    paddingX={'4px'}
                    marginTop={'20px'}
                    marginBottom={'16px'}
                    gap={'10px'}
                  >
                    <Text
                      fontSize={'18px'}
                      lineHeight={'22px'}
                      variant={'urbanistSmall'}
                      color={'text.darkBlue'}
                      fontWeight={'600'}
                      marginBottom={'10px'}
                    >
                      {displayCapitalizedUnderscoredString(key)}
                    </Text>
                    {value?.questions?.map((question) => {
                      const responseValue = question.value as number;
                      const totalScoreX10 = responseValue > 10 ? responseValue : responseValue * 10;

                      return (
                        <HStack gap={'14px'} key={question.id ?? question.inputId}>
                          <CircleFillOutWithText
                            circleDiameter={54}
                            svgId={`screener-${question?.id ?? question?.inputId}`}
                            thickness={4}
                            percentageTextStyle={{ fontSize: '12px', fontWeight: '700' }}
                            percentageFilled={totalScoreX10}
                            circleColor={getColorFromPercentage(totalScoreX10)}
                            boxShadow={'0px 3px 6px 0px #0E006224'}
                            customPercentageText={question?.value.toString()}
                          />
                          <Text
                            fontSize={'16px'}
                            lineHeight={'20px'}
                            variant={'urbanistSmall'}
                            color={'text.mediumBlue'}
                          >
                            {question.question}
                          </Text>
                        </HStack>
                      );
                    })}
                  </VStack>
                );
              })}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </VStack>
    </VStack>
  );
};

export default SingleDomain;
